// Shadow-DOM stylesheet. Theme switching is a class on .vf-root; every
// themed rule reads a custom property defined per theme class.
//
// Single-frame law: .vf-root itself carries the panel background, 1px
// frame-token border, and border-radius — every element (header, answer
// band, chart, pin lane, drilldown, footer) is a descendant of
// .vf-root, so it renders inside one bounded card. No element may render
// outside this border; internal separation
// stays hairlines (border-bottom on header/band, not a second
// outer frame).
//
// Typography scale (whole pixels only, weights 400/500/600):
// 26/600 mono = answer-band headline figure (the one sanctioned exception
//   to the 15/600 value tier below — same label/value/detail skeleton,
//   larger size only);
// 15/600 = answer-band cell values (mono) and the header title (sans);
// 13/400 mono = a SEPARATE, unrelated tier: pin-card rows,
//   status/error text — none of these are answer-band cells, so the
//   band's value-size unification does not touch them;
// 12/400 = answer-band cell details, legend text, axis date row, prose/controls;
// 11/500 = answer-band cell labels;
// 11/400 mono = axis epoch ticks, answer-band subdetail (demoted ordinal facts);
// 10/600 uppercase = group eyebrow (pin-card group label, tooltip group header);
// 10/400 = chart-internal micro labels (dataZoom slider ticks,
//   reference-line labels in consistency.ts).
//
// Spacing scale: 4/8/16/24/32, applied throughout this file. ECharts grid
// gutters (chart-options.ts GRID_LEFT/GRID_RIGHT) are axis-fit geometry,
// exempt from this scale. Border-radius and box-shadow blur values follow
// their own (smaller, decorative) scale, not this one.
//
// Control targets: button.vf-btn min-height/min-width is 24px — below the
// 44px touch-platform guidance. Recorded trade-off: this is a desk-bound
// institutional instrument (the ECharts slider handles are smaller still);
// 24px is accepted deliberately, not by omission.

export const STYLES = `
:host {
  display: block;
  font-family: -apple-system, "Segoe UI", "Helvetica Neue", Arial, sans-serif;
}
.vf-mono {
  font-family: ui-monospace, "SF Mono", "Cascadia Code", "Roboto Mono", Consolas, monospace;
  font-variant-numeric: tabular-nums;
}
.vf-root {
  position: relative;
  box-sizing: border-box;
  --vf-text: #16181D;
  --vf-muted: #5B6472;
  --vf-border: #C2C8D1;
  --vf-surface: #ffffff;
  --vf-surface-raised: #ffffff;
  /* --vf-accent is CHROME namespace (focus rings, active-tab underline,
     explorer row outlines) — numerically equal to theme.ts medianLine
     today but not derived from it; the series ink and this accent retune
     independently, same decoupling rule as the channel aliases in
     theme.ts. */
  --vf-accent: #1f77b4;
  --vf-amber: #C2410C;
  --vf-error: #b0413e;
  --vf-policy-note: #047857;
  --vf-shadow: 0 2px 10px rgba(0, 0, 0, 0.14);
  display: block;
  border: 1px solid var(--vf-border);
  border-radius: 6px;
  background: var(--vf-surface-raised);
  padding: 16px;
  color: var(--vf-text);
  line-height: 1.35;
}
.vf-root.dark {
  --vf-text: #e6e6e6;
  --vf-muted: #9a9a9a;
  --vf-border: #454b52;
  --vf-surface: transparent;
  --vf-surface-raised: #22252a;
  --vf-accent: #4c9bd4;
  --vf-amber: #e0a83e;
  --vf-error: #e0645c;
  --vf-policy-note: #34d399;
  --vf-shadow: 0 2px 12px rgba(0, 0, 0, 0.55);
}
.vf-hidden {
  display: none !important;
}
/* Off-screen but announced: the polite live region for keyboard epoch
   moves and other AT-only output. display:none would silence it. */
.vf-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
button.vf-btn {
  border: 1px solid var(--vf-border);
  border-radius: 3px;
  background: transparent;
  color: var(--vf-muted);
  font: inherit;
  font-size: 12px;
  line-height: 1;
  min-height: 24px;
  min-width: 24px;
  padding: 4px 8px;
  cursor: pointer;
  /* A label never breaks inside its own box ("validator/income" two-lined
     under width pressure); a button that does not fit moves whole to the
     header's wrap rung row instead. */
  white-space: nowrap;
}
button.vf-btn:hover {
  color: var(--vf-text);
  border-color: var(--vf-muted);
}
button.vf-btn:focus-visible,
.vf-header-btn:focus-visible {
  outline: 2px solid var(--vf-accent);
  outline-offset: 1px;
}
/* Icon-only close control: square padding, glyph centered, no text
   line-box (the cross svg carries the ink via currentColor). */
button.vf-btn.vf-close {
  padding: 6px;
  line-height: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
button.vf-btn[disabled] {
  opacity: 0.45;
  cursor: not-allowed;
}
@media (prefers-reduced-motion: reduce) {
  .vf-pin-card {
    transition: none !important;
  }
  .vf-track-cursor-blink {
    animation: none !important;
  }
}

/* Status / error */
.vf-status {
  padding: 24px 16px;
  font-size: 13px;
  color: var(--vf-muted);
}
.vf-error {
  margin: 16px 0;
  padding: 8px 14px;
  border: 1px solid var(--vf-error);
  border-left-width: 4px;
  border-radius: 3px;
  font-size: 13px;
  line-height: 1.5;
  color: var(--vf-text);
}
.vf-error .vf-error-title {
  font-weight: 600;
  margin-bottom: 4px;
}

/* Header: one 40px row above the wrap rung; below it (vf-header-wrap,
   host width < 620) the row trades fixed height for wrapping so the
   control cluster lands on a second row instead of overflowing the
   frame — the same policy the embedded explorer header always had. */
.vf-header {
  display: flex;
  align-items: center;
  gap: 16px;
  /* min-height, not height: at fine pointer the content is shorter and
     the row renders exactly 40px as always; coarse-pointer 44px touch
     controls need the row to grow with them — fixed 40 sat them ON the
     border-bottom hairline (operator landscape screenshot 2026-07-17). */
  min-height: 40px;
  padding: 0 4px;
  border-bottom: 1px solid var(--vf-border);
}
.vf-header-title {
  font-size: 15px;
  font-weight: 600;
  white-space: nowrap;
}
/* Explicit, always-present separator between title and metric — belt and
   suspenders alongside the header's flex gap, so the two text runs can
   never read as one concatenated string (the run-on bug the operator
   observed: visual-only spacing does not survive every render path). */
.vf-header-sep {
  color: var(--vf-muted);
  flex: none;
}
.vf-header-meta {
  font-size: 12px;
  color: var(--vf-muted);
  white-space: nowrap;
}
.vf-header-controls {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 8px;
}
/* Theme toggle: same .vf-btn box as its neighbors; the inline SVG glyph
   (sun/moon, hairline stroke) names the theme a click switches TO. */
.vf-theme-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
}
.vf-theme-toggle svg {
  display: block;
}
/* Width-collapse rung: the host JS toggles vf-collapsed-metric on
   .vf-header below its width threshold. The separator hides with the meta —
   hiding the meta alone leaves an orphan "·". */
.vf-header.vf-collapsed-metric .vf-header-meta,
.vf-header.vf-collapsed-metric .vf-header-sep {
  display: none;
}
/* Wrap rung (host width < 620): auto height + wrap; min-height keeps the
   one-row case pixel-identical to the fixed frame. The controls give up
   their right-edge anchor so the wrapped row starts at the frame's left
   edge, in reading direction, and can subdivide further on its own at
   very narrow widths. */
.vf-header.vf-header-wrap {
  flex-wrap: wrap;
  height: auto;
  min-height: 40px;
  row-gap: 8px;
  padding: 8px 4px;
}
.vf-header.vf-header-wrap .vf-header-controls {
  margin-left: 0;
  flex-wrap: wrap;
  row-gap: 8px;
}
/* Phone rung: at the desk gap and padding the control cluster measures
   ~353px — past a 360px host's ~344px content box — which orphaned the
   theme toggle onto a third row. Chrome yields, content never does: the
   inter-button gap drops 8->6 and the buttons' side padding 8->6 (the
   labels are untouched), bringing the cluster to ~330px. Header scope
   only — pin-card and panel buttons keep the desk box. */
/* Phone rung: the header trades the desk toolbar for a grid grammar —
   title row, then the view switch as a full-width tab bar (equal
   segments), then [validator income] [explorer] [theme toggle] as
   tiles filling their row. Every row shares the same left/right edges:
   aligned ink instead of wrapped boxes of accidental widths ("compost
   of random elements", operator 2026-07-17). Packing one row was also
   a few-pixel gamble that orphaned single buttons — deterministic rows
   beat pixel luck. */
.vf-root.vf-phone .vf-header-controls {
  flex: 1 1 100%;
  gap: 8px 4px;
}
.vf-root.vf-phone .vf-view-switch {
  flex: 1 0 100%;
}
.vf-root.vf-phone .vf-view-switch .vf-view-btn {
  flex: 1 1 0;
}
.vf-root.vf-phone .vf-header-controls .vf-header-btn {
  flex: 1 1 0;
}
.vf-root.vf-phone .vf-header-controls button.vf-btn {
  padding: 4px 6px;
}
.vf-root.vf-phone .vf-header-controls .vf-theme-toggle {
  padding: 4px;
}
/* Phone rung: the theme toggle is the LAST TILE of the surface row —
   [validator income | explorer | toggle] — never a floater beside the
   title (a corner dock read as a non-symmetric stray against the
   aligned rows beneath it, operator screenshot 2026-07-17). The title
   demotes to a compact quiet line: it still carries the measurement
   claim, but the tab bar is the header's primary control. */
.vf-root.vf-phone .vf-header-title {
  font-size: 13px;
}

/* Answer band: hairline-separated cells sharing one label/value/detail
   grammar. Wrap policy: below the width at which every cell fits on one
   row, cells wrap two per row in DOM order (nth-child(odd) starts each
   row and drops its left hairline); the band never scrolls horizontally. */
.vf-band {
  display: flex;
  /* stretch, not flex-start: every cell box extends to the band's common
     height, so the tab underlines (inset bottom shadows) and the hairline
     cell separators all land on ONE shared baseline. With flex-start each
     cell ended at its own content and the active group's underline sat at
     three different heights — a ragged rule no printed page would show. */
  align-items: stretch;
  flex-wrap: wrap;
  /* row-gap only: it costs nothing while the band is on one row, and gives
     wrapped rows their separation without the browser choosing it. Column
     spacing stays the cells' own chosen padding + hairline. */
  gap: 8px 0;
  padding: 8px 4px;
  border-bottom: 1px solid var(--vf-border);
}
.vf-band-cell {
  box-sizing: border-box;
  padding: 0 16px;
  border-left: 1px solid var(--vf-border);
  /* min-width: 0 overrides the flex-item default (min-width: auto), which
     otherwise refuses to shrink below the intrinsic width of unbroken cell
     content (a long band-detail string) — that refusal is what pushes the
     band, and with it .vf-root, past the frame (single-frame law). */
  min-width: 0;
}
.vf-band-cell:first-child {
  padding-left: 4px;
  border-left: none;
}
/* Wrap rung, driven by the HOST width via #applyCollapseLadder — not by a
   media query. A media query inside a shadow root measures the VIEWPORT, so
   an embed narrowed inside a wide page (a staking desk's column: exactly the
   case this widget exists for) wrapped with no row gap and an orphan left
   hairline, and the rescue never fired. Every other responsive rung already
   measures the host through the ResizeObserver; this one now joins it. */
.vf-band.vf-band-narrow .vf-band-cell {
  flex: 1 1 50%;
}
.vf-band.vf-band-narrow .vf-band-cell:nth-child(odd) {
  border-left: none;
  padding-left: 4px;
}
/* Phone rung (host width < 430, always alongside vf-band-narrow): the
   headline cell takes the whole first row, so the five cells pack
   [headline] [2,3] [4,5] with no orphan. Row starts move from the odd
   cells to the even ones, so the hairline logic re-derives: odd cells
   (3, 5) regain their left hairline, even cells (2, 4) and the headline
   drop theirs. Source order carries the override — the narrow rules
   above tie on specificity. */
.vf-band.vf-band-phone .vf-band-cell:first-child {
  flex: 1 1 100%;
}
.vf-band.vf-band-phone .vf-band-cell:nth-child(odd) {
  border-left: 1px solid var(--vf-border);
  padding-left: 16px;
}
.vf-band.vf-band-phone .vf-band-cell:nth-child(even),
.vf-band.vf-band-phone .vf-band-cell:first-child {
  border-left: none;
  padding-left: 4px;
}
/* Emergency stack rung (host width < 320 — below the smallest
   mainstream phone): the band goes one cell per row. The documented
   exception to the no-sprawl law: beneath the supported 320px floor,
   a legible single column outranks an unreadable pair. */
.vf-band.vf-band-stack .vf-band-cell,
.vf-band.vf-band-stack .vf-band-cell:nth-child(odd),
.vf-band.vf-band-stack .vf-band-cell:nth-child(even) {
  flex: 1 1 100%;
  border-left: none;
  padding-left: 4px;
}
/* Frame padding rungs, on the root with the same thresholds: the frame
   yields chrome before content ever shrinks (space law: density by
   layout). Same specificity, so source order lets the phone rung win
   while both classes are present. */
.vf-root.vf-compact {
  padding: 12px;
}
.vf-root.vf-phone {
  padding: 8px;
}
/* Freshness line ("through epoch N · ended ..."): rendered below the
   plot on every pass, DISPLAYED only while the collapsed-metric rung
   hides the header meta — narrowing relocates the fact instead of
   deleting it. */
.vf-freshness {
  display: none;
}
.vf-root.vf-meta-collapsed .vf-freshness {
  display: block;
}
.vf-band-label {
  font-size: 11px;
  font-weight: 500;
  color: var(--vf-muted);
}
/* Cell 5 (net incl MEV) only: swatch inline before the label text. */
.vf-band-label-swatch {
  display: flex;
  align-items: center;
  gap: 4px;
}
.vf-band-swatch {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  flex: none;
}
.vf-band-value {
  margin-top: 2px;
  font-size: 15px;
  font-weight: 600;
  /* Same overflow guard as .vf-band-detail: the value tier now carries the
     economic string ("+2.82 SOL / 1k SOL / yr"), the band's widest, so it
     must wrap inside its cell rather than push .vf-root past the frame. */
  overflow-wrap: anywhere;
}
.vf-band-value-lg {
  margin-top: 2px;
  font-size: 26px;
  font-weight: 600;
}
.vf-band-detail {
  margin-top: 2px;
  font-size: 12px;
  font-weight: 400;
  color: var(--vf-muted);
  /* Overflow guard: wrap within the cell rather than push past the frame
     (single-frame law) — the full detail text always stays readable. */
  overflow-wrap: anywhere;
}
.vf-band-subdetail {
  /* 8px, not 2px: the ordinal is not part of the label/value/detail type
     stack above it, and at 11px a 1px size step is not a demotion the eye
     can read. The gap is the demotion — spatial separation, using the
     scale's own step, rather than an opacity drop that would take
     --vf-muted below the 4.5:1 floor this project enforces on itself. */
  margin-top: 8px;
  font-size: 11px;
  font-weight: 400;
  color: var(--vf-muted);
}
.vf-band-placeholder {
  font-size: 12px;
  color: var(--vf-muted);
  padding: 8px 4px;
}

/* Live region (phase 3, operator redesign): ONE compact activatable row
   directly under the band — the leader track (flex-grow, the dominant
   element) with the ETA text to its right (role=button in JS; activation
   expands the blocks datastream panel below the plot; the verbose
   sentence lives in that panel now). The region carries the bottom
   hairline toward the chart; its top hairline IS the band's existing
   border-bottom. Visibility (empty-until-first-poll, hidden under the
   explorer) is JS-managed via vf-hidden. */
.vf-live-region {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 8px;
  border-bottom: 1px solid var(--vf-border);
  cursor: pointer;
}
.vf-live-region:focus-visible {
  outline: 2px solid var(--vf-accent);
  outline-offset: -2px;
}
/* Text-part grammar (shared by the compact ETA and the panel's summary
   line): muted labels; numerals in text ink, mono + tabular-nums
   (.vf-live-num pairs with .vf-mono); stale age in amber. */
.vf-live-label {
  color: var(--vf-muted);
}
.vf-live-num {
  color: var(--vf-text);
}
.vf-live-sep {
  color: var(--vf-muted);
  padding: 0 4px;
}
.vf-live-stale {
  color: var(--vf-amber);
}
/* Compact-row ETA text: never wraps, never shrinks — the track absorbs
   the width. Empty (next present with unusable ETA) renders nothing. */
.vf-live-eta {
  flex: none;
  font-size: 12px;
  font-weight: 400;
  color: var(--vf-muted);
  white-space: nowrap;
}
.vf-live-eta:empty {
  display: none;
}
/* Leader progress track: 8px ambient SVG bar, the row's dominant element.
   Ink law: produced = text ink; skipped/abandoned/unavailable =
   --vf-error (the one sanctioned red — a factual failure mark); scheduled
   future = muted at reduced opacity; the "now" cursor = the positive
   green (--vf-policy-note; it marks OUR position, a positive fact —
   operator direction, phase 3). */
.vf-live-track {
  flex: 1 1 auto;
  min-width: 0;
  line-height: 0;
}
.vf-live-track:empty {
  display: none;
}
.vf-track {
  display: block;
  width: 100%;
  height: 8px;
}
.vf-track-line {
  fill: var(--vf-border);
}
.vf-track-produced {
  fill: var(--vf-text);
}
.vf-track-failed {
  fill: var(--vf-error);
}
.vf-track-scheduled {
  fill: var(--vf-muted);
  opacity: 0.45;
}
.vf-track-cursor {
  fill: var(--vf-policy-note);
}
/* Imminent-leader pulse (JS toggles the class: remaining ETA <= 300 s AND
   the data is not stale — stale data never blinks). Presentation only:
   opacity animation, no layout shift. Killed under reduced motion (see
   the prefers-reduced-motion block above). */
.vf-track-cursor-blink {
  animation: vf-cursor-pulse 1.2s ease-in-out infinite;
}
@keyframes vf-cursor-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}

/* Answer-band view tabs (base mode): the five cells act as
   three view selectors — base (cell 1), position (cells 2-4), mev (cell 5).
   Active group: 2px accent underline as an inset shadow (no layout shift);
   hover previews the same underline in the hairline token. Existing theme
   tokens only — no new colors. */
.vf-band-tab {
  cursor: pointer;
  padding-bottom: 4px;
  /* Resting hairline underline: at rest nothing else distinguishes a tab
     cell from a static statistic, so the band bottom reads as a segmented
     control row; the 2px accent stays reserved for the active group. */
  box-shadow: inset 0 -1px 0 var(--vf-border);
}
/* Hover sits between the resting hairline and the active accent: full
   2px weight in muted ink — the underline strengthens toward the active
   treatment without spending the accent, which stays reserved for the
   active group. */
.vf-band-tab:hover {
  box-shadow: inset 0 -2px 0 var(--vf-muted);
}
.vf-band-tab:focus-visible {
  outline: 2px solid var(--vf-accent);
  outline-offset: 1px;
}
.vf-band-tab-active,
.vf-band-tab-active:hover {
  box-shadow: inset 0 -2px 0 var(--vf-accent);
}

/* Caption below the band / chart. max-width: an 11px line running the
   full 1200px harness width (~200 characters) skims as texture; 72ch is
   a readable measure. Applies to every footnote/caption prose class. */
.vf-caption {
  font-size: 11px;
  color: var(--vf-muted);
  padding: 4px 4px 0;
  line-height: 1.35;
  max-width: 72ch;
}

/* Chart */
.vf-chart-wrap {
  position: relative;
}
/* Plot-corner segmented controls: joined .vf-btn segments at the plot's
   top-right, inside the grid's 40px top margin — the income view's own
   legend (2+ visible lines) anchors top-LEFT, so this corner stays
   chrome-only. The view switch shares the segment grammar but lives in
   the header controls row: the base view's legend owns the plot's top
   margin, so any overlay there collides with it (operator screenshot,
   2026-07-14). The channel switch is MULTI-select (each segment toggles
   its line); pressed still means "on" — same ink language. The pressed
   segment carries the text ink and sits on top of the shared border
   (band-tab language: state marked by ink, not new color). */
.vf-income-controls {
  position: absolute;
  top: 6px;
  right: 16px;
  z-index: 2;
  display: flex;
  gap: 8px;
  align-items: flex-start;
}
/* Chart-compact rung (host width < 700, the chart geometry tier): the
   corner overlay becomes a static in-flow row above the plot — at
   narrow widths (and doubly so with coarse-pointer 44px targets) the
   absolute overlay sat on top of the legend's canvas rows. The DOM
   already orders it before the chart host, so static = reading
   direction. The income option builder moves the legend to the plot's
   top-left in the same tier, reclaiming the vacated overlay row. */
.vf-root.vf-chart-compact .vf-income-controls {
  position: static;
  flex-wrap: wrap;
  row-gap: 6px;
  padding: 0 4px 6px;
}
/* Phone rung: the income controls take the header's grid grammar — the
   unit and channel switches stretch to fill the row (grow weighted by
   segment count so every segment renders equal), the [x] keeps its
   natural size at the right edge. One aligned bar, not floating boxes. */
.vf-root.vf-phone .vf-income-controls .vf-unit-switch {
  flex: 2 1 0;
}
.vf-root.vf-phone .vf-income-controls .vf-channel-switch {
  flex: 4 1 0;
}
.vf-root.vf-phone .vf-income-controls .vf-unit-switch .vf-unit-btn,
.vf-root.vf-phone .vf-income-controls .vf-channel-switch .vf-channel-btn {
  flex: 1 1 0;
}
.vf-unit-switch,
.vf-channel-switch,
.vf-view-switch {
  display: flex;
}
.vf-unit-switch .vf-unit-btn,
.vf-channel-switch .vf-channel-btn,
.vf-view-switch .vf-view-btn {
  border-radius: 0;
}
.vf-unit-switch .vf-unit-btn:first-child,
.vf-channel-switch .vf-channel-btn:first-child,
.vf-view-switch .vf-view-btn:first-child {
  border-radius: 3px 0 0 3px;
}
.vf-unit-switch .vf-unit-btn:last-child,
.vf-channel-switch .vf-channel-btn:last-child,
.vf-view-switch .vf-view-btn:last-child {
  border-radius: 0 3px 3px 0;
}
.vf-unit-switch .vf-unit-btn + .vf-unit-btn,
.vf-channel-switch .vf-channel-btn + .vf-channel-btn,
.vf-view-switch .vf-view-btn + .vf-view-btn {
  margin-left: -1px;
}
.vf-unit-switch .vf-unit-btn[aria-pressed="true"],
.vf-channel-switch .vf-channel-btn[aria-pressed="true"],
.vf-view-switch .vf-view-btn[aria-pressed="true"] {
  color: var(--vf-text);
  border-color: var(--vf-muted);
  position: relative;
  z-index: 1;
}
.vf-chart {
  width: 100%;
  height: var(--vf-chart-height, 448px);
}
/* The plot is a keyboard stop (role=application, arrow keys/Enter): the
   ring renders inside the frame (negative offset), matching the explorer
   rows' convention. */
.vf-chart:focus-visible {
  outline: 2px solid var(--vf-accent);
  outline-offset: -2px;
}

/* Pin lane: docked flow layout, never floating in-plot. Empty (the
   default state — PinLayer only ever appends the single card and removes
   it on dismiss/destroy), the lane spends no vertical: unconditional
   padding put a permanent 16px blank line between chart and footer. */
.vf-pin-lane {
  padding: 8px 4px;
}
.vf-pin-lane:empty {
  padding: 0;
}
.vf-pin-card {
  border: 1px solid var(--vf-border);
  border-left: 2px solid var(--vf-amber);
  border-radius: 4px;
  background: var(--vf-surface-raised);
  box-shadow: var(--vf-shadow);
  padding: 8px;
}
.vf-pin-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.vf-pin-title {
  font-size: 12px;
  font-weight: 600;
  flex: 1;
}
.vf-pin-row {
  display: flex;
  justify-content: space-between;
  gap: 16px;
  font-size: 13px;
  padding: 2px 0;
}
.vf-pin-value {
  font-family: ui-monospace, "SF Mono", "Cascadia Code", "Roboto Mono", Consolas, monospace;
  font-variant-numeric: tabular-nums;
}
.vf-pin-group-label {
  font-size: 10px;
  font-weight: 600;
  color: var(--vf-muted);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 8px 0 2px;
}
.vf-pin-group-label:not(:first-child) {
  padding-top: 8px;
  border-top: 1px solid var(--vf-border);
}
.vf-pin-details {
  margin-top: 8px;
}

/* Drill-down panel. Alignment: label/prose columns left; PURE-NUMERIC
   columns right (Lamports/SOL) — place-value alignment is what
   tabular-nums exists for. overflow-x: the nowrap table (widest row: label +
   lamports + SOL + inline policy note) cannot shrink below min-content;
   below that width it scrolls inside the panel instead of pushing past
   the frame border (single-frame law — same class of guard as the band's
   min-width: 0). */
.vf-drilldown {
  margin: 8px 4px 8px;
  border: 1px solid var(--vf-border);
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  overflow-x: auto;
}
.vf-drilldown-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.vf-drilldown-title {
  font-weight: 600;
  flex: 1;
}
/* Content-width, left-edge aligned with the panel text (matches the blocks
   datastream table): a full-width stretch on three narrow columns opens the
   same arbitrary mid-table void. */
.vf-drilldown table {
  width: auto;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}
.vf-drilldown th,
.vf-drilldown td {
  text-align: left;
  padding: 5px 12px;
  border-bottom: 1px solid var(--vf-border);
  white-space: nowrap;
}
.vf-drilldown th:first-child,
.vf-drilldown td:first-child {
  padding-left: 0;
}
.vf-drilldown th {
  font-weight: 600;
  color: var(--vf-muted);
}
/* Pure-numeric columns (Lamports, SOL) right-align so magnitudes compare
   by place value down the column — the printed-ledger convention. The
   Channel column stays left. */
.vf-drilldown th:nth-child(2),
.vf-drilldown td:nth-child(2),
.vf-drilldown th:nth-child(3),
.vf-drilldown td:nth-child(3) {
  text-align: right;
}
.vf-drilldown tr.vf-net td {
  font-weight: 600;
  border-top: 2px solid var(--vf-muted);
}
/* Policy-state note: a measured-zero commission read as a delegator-facing
   fact. Inline inside the SOL cell — a dedicated <td> would become a
   table-wide fourth column and open a void gutter on every noteless row.
   Floated left within the now right-aligned SOL cell so the SOL figure
   keeps its flush-right place-value alignment on note-carrying rows. */
.vf-drilldown span.vf-drilldown-note {
  float: left;
  font-size: 12px;
  font-weight: 400;
  color: var(--vf-policy-note);
  margin-right: 16px;
}
.vf-drilldown .vf-meta {
  margin-top: 8px;
  font-size: 11px;
  color: var(--vf-muted);
  line-height: 1.5;
}

/* Blocks datastream panel: same below-the-plot detail grammar as the
   drill-down (border, radius, padding, nowrap table scrolling inside the
   panel rather than pushing past the frame). Numeric columns (slot, fees,
   tips) right-align via .vf-num — place-value alignment down the column;
   the time column stays left. */
.vf-stream {
  margin: 8px 4px 8px;
  border: 1px solid var(--vf-border);
  border-radius: 4px;
  padding: 8px;
  font-size: 12px;
  overflow-x: auto;
}
.vf-stream-head {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}
.vf-stream-title {
  font-weight: 600;
  flex: 1;
}
/* The running epoch's live summary sentence — the panel's first line
   (phase 3: moved here from the main-view strip). Same text-part grammar
   as the quality row (.vf-live-label / .vf-live-num / .vf-live-stale). */
.vf-stream-summary {
  margin-bottom: 8px;
  color: var(--vf-muted);
}
.vf-stream-summary:empty {
  display: none;
}
.vf-stream-quality {
  margin-bottom: 8px;
}
.vf-stream-quality .vf-live-sep {
  padding: 0 4px;
}
/* Content-width, not full-width: 4 narrow columns stretched to 100% leave
   the browser to dump all slack into arbitrary inter-column gaps (a void
   left of the right-aligned slot, a cavern mid-table, tips against the
   frame). Sized to content with even gutters, the group reads as a ledger
   block. Left edge aligns with the panel's text lines; the right margin is
   intentional whitespace, not the browser's leftover. */
.vf-stream table {
  width: auto;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;
}
.vf-stream th,
.vf-stream td {
  text-align: left;
  padding: 5px 12px;
  border-bottom: 1px solid var(--vf-border);
  white-space: nowrap;
}
/* First cell carries no left inset: the slot column's left edge lines up
   with the title, summary, and quality lines above it. */
.vf-stream th:first-child,
.vf-stream td:first-child {
  padding-left: 0;
}
/* One deliberate gutter between the identity group (slot, time) and the
   money group (fees, tips) — the only wide gap in the table, and a chosen
   one. */
.vf-stream th:nth-child(3),
.vf-stream td:nth-child(3) {
  padding-left: 28px;
}
/* Phone rung: the table's chrome (cell padding, the deliberate group
   gutter) tightens so slot/time/fees/tips fit the frame without
   engaging the scroll container — content and fonts untouched. */
.vf-root.vf-phone .vf-stream th,
.vf-root.vf-phone .vf-stream td {
  padding: 5px;
}
.vf-root.vf-phone .vf-stream th:first-child,
.vf-root.vf-phone .vf-stream td:first-child {
  padding-left: 0;
}
.vf-root.vf-phone .vf-stream th:nth-child(3),
.vf-root.vf-phone .vf-stream td:nth-child(3) {
  padding-left: 12px;
}
.vf-stream th {
  font-weight: 600;
  color: var(--vf-muted);
}
.vf-stream th.vf-num,
.vf-stream td.vf-num {
  text-align: right;
}
.vf-stream tr.vf-stream-row {
  cursor: pointer;
}
.vf-stream tr.vf-stream-row:hover td {
  color: var(--vf-text);
}
.vf-stream tr.vf-stream-row:focus-visible {
  outline: 2px solid var(--vf-accent);
  outline-offset: -2px;
}
.vf-stream-more {
  margin-top: 4px;
  font-size: 11px;
  color: var(--vf-muted);
}
.vf-stream-note {
  color: var(--vf-muted);
}

/* Footer */
.vf-footer {
  margin-top: 4px;
  padding: 0 4px 8px;
  line-height: 1.35;
  color: var(--vf-muted);
}
.vf-footer-note {
  font-size: 11px;
  /* Provenance line, not prose: exempt from the 72ch reading cap so it
     sits on one line at widget widths (operator C55). */
}

/* Coarse-pointer adaptation. Pointer class is a CAPABILITY axis,
   orthogonal to the width ladder: a phone-width mouse embed keeps the
   desk targets, a desktop-width touch tablet gets these. The 24px
   minimum above is the recorded desk-bound trade-off (fine pointer);
   this block is the case that decision explicitly excluded, not a
   reversal of it. A non-width media feature is shadow-DOM-safe — only
   width queries misread the viewport for the host. */
@media (pointer: coarse) {
  button.vf-btn,
  .vf-unit-switch .vf-unit-btn,
  .vf-channel-switch .vf-channel-btn,
  .vf-view-switch .vf-view-btn {
    min-height: 44px;
    min-width: 44px;
  }
  /* In-table copy chips are secondary affordances riding inside rows
     that are themselves tap targets: a 44px chip dwarfs its 12px row
     (operator screenshot 2026-07-17) — 32px keeps a real touch floor
     without wrecking table density, and the identifier centers against
     the taller chip instead of hanging off its top edge. */
  button.vf-btn.vf-copy {
    min-height: 32px;
    min-width: 32px;
  }
  .vf-ident-wrap {
    align-items: center;
  }
  /* Touch controls fill the header's 40px minimum and would kiss the
     border-bottom without their own breathing room. */
  .vf-header {
    padding-top: 6px;
    padding-bottom: 6px;
  }
}
`;
